import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import Loading from "./Components/Loading/Loading";
import "./app.scss";
import "react-datepicker/dist/react-datepicker.css";
import ProtectedRoutes from "./Components/ProtectedRoutes/ProtectedRoutes";
import axios from "axios";
import qz from "qz-tray";
import { toast } from "react-toastify";

// Page Loader Component
const PageLoader = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    setLoading(true);
    NProgress.start();

    const timeout = setTimeout(() => {
      setLoading(false);
      NProgress.done();
    }, 500); // Simulate loading delay

    return () => clearTimeout(timeout);
  }, [location]);

  return loading ? <div className="page-loader" /> : null;
};

const Login = lazy(() => import("./Pages/Login"));
const Home = lazy(() => import("./Pages/Home"));
const SorterSession = lazy(() => import("./Pages/SorterSession"));
const AddSession = lazy(() => import("./Pages/AddSession"));
const DockSummary = lazy(() => import("./Pages/DockSummary"));
const AddDock = lazy(() => import("./Pages/AddDock"));
const Settings = lazy(() => import("./Pages/Settings"));
const SortationPlan = lazy(() => import("./Pages/SortationPlan"));
const ActiveSession = lazy(() => import("./Pages/ActiveSession"));
const Creation = lazy(() => import("./Pages/BoxCreation"));
const Summary = lazy(() => import("./Pages/Summary"));

const Layout = () => {
  const [printerList, setPrinterList] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState("");

  useEffect(() => {
    // signed certificate for qz tray
    qz.security.setCertificatePromise(function (resolve, reject) {
      //Preferred method - from server
      fetch("digital-certificate.txt", {
        cache: "no-store",
        headers: { "Content-Type": "text/plain" },
      }).then(function (data) {
        data.ok ? resolve(data.text()) : reject(data.text());
      });

      //Alternate method 2 - direct
      resolve(
        "-----BEGIN CERTIFICATE-----\n" +
          "MIID+TCCAuGgAwIBAgIUFJx1QkZbDOHgHa+ec24vw3o1txIwDQYJKoZIhvcNAQEL\n" +
          "BQAwgYoxCzAJBgNVBAYTAklOMQ8wDQYDVQQIDAZQdW5qYWIxETAPBgNVBAcMCEx1\n" +
          "ZGhpYW5hMQ8wDQYDVQQKDAZTZWVrZXgxEzARBgNVBAsMClNldmVucm9ja3MxDDAK\n" +
          "BgNVBAMMA0dydjEjMCEGCSqGSIb3DQEJARYUZ3J2LndlYmRldkBnbWFpbC5jb20w\n" +
          "IBcNMjMwOTIzMDUwNjMxWhgPMjA1NTAzMTgwNTA2MzFaMIGKMQswCQYDVQQGEwJJ\n" +
          "TjEPMA0GA1UECAwGUHVuamFiMREwDwYDVQQHDAhMdWRoaWFuYTEPMA0GA1UECgwG\n" +
          "U2Vla2V4MRMwEQYDVQQLDApTZXZlbnJvY2tzMQwwCgYDVQQDDANHcnYxIzAhBgkq\n" +
          "hkiG9w0BCQEWFGdydi53ZWJkZXZAZ21haWwuY29tMIIBIjANBgkqhkiG9w0BAQEF\n" +
          "AAOCAQ8AMIIBCgKCAQEAjit0o5WvhQInxccqj7/Xtqgroq6X61gWI/gSQRfpbdMM\n" +
          "6U1MqGW8qzzR5bX+VBURXcdEFokpcxY3/oz17VjPQ0D/bMensZ+EqQubQqmM56zS\n" +
          "BdMlrITCoSbT+cNvA0gB4U4d8UQ58vzZHY0zj6lNFjCZuDfHO+DG+rD9EwjngZzU\n" +
          "+rS8jOb2y7xMu5tuncku/EeEESv1LndGhAmRAfidOUDAL4zbTdLQvz9zP8xcGzas\n" +
          "UhsTBfQ9r4e1b2LKgyIm08oY/eg2qjGWyfPfbrXrzsWpnyJwdHldHMUAAnvdcCxH\n" +
          "bcXSl1upy1wAq1WO4OpPkRlnRLqC09qGWnRAyU4fUQIDAQABo1MwUTAdBgNVHQ4E\n" +
          "FgQU/AtLKKkV14bzAAu99sUVO64c3DYwHwYDVR0jBBgwFoAU/AtLKKkV14bzAAu9\n" +
          "9sUVO64c3DYwDwYDVR0TAQH/BAUwAwEB/zANBgkqhkiG9w0BAQsFAAOCAQEAAA8B\n" +
          "aLZoOZ1w8SVNVDlpSSgq4+Py917Wp1Vp7D6GN6QL0PIO0Y+k6osFXQuWCNzjjSYp\n" +
          "BO7xnr4eHxSlJ9xh4s8d5RlVhCG+F8oRNeIAFsUjj4RLy/kvJWnJI5jcmjZCKhsS\n" +
          "zKGAiki6D4vhePGU+kzezOubGN0YJC5pVpwvTdJLXdiu77IwfTYSMRuxOu9wvhhk\n" +
          "oq/HKpBIYDr5b6frej6H9/o4mCN92qZulhF0bsYhDkvE/4S9cwDaX0i9bV7Ojt17\n" +
          "FCabjHwxjgycLavqJiXCFyfRoF8CyckAVzqMyuVcCP8/3wCM603KB41+LP+c6Hds\n" +
          "D1DnIqq9EEUabJvMyQ==\n" +
          "-----END CERTIFICATE-----\n"
      );
    });

    qz.security.setSignatureAlgorithm("SHA512"); // Since 2.1
    qz.security.setSignaturePromise(function (toSign) {
      return function (resolve, reject) {
        // Preferred method - from server
        axios
          .get(
            "https://erp.sevenrocks.in/app/webroot/qz-local/sign-message.php",
            {
              params: { request: toSign },
              headers: { "Content-Type": "text/plain" },
            }
          )
          .then(function (response) {
            if (response.status === 200) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          })
          .catch(function (error) {
            reject(error);
          });

        // Alternate method - unsigned
        // resolve(); // remove this line in the live environment
      };
    });

    // Connect and get printers
    qz.websocket
      .connect()
      .then(() => {
        return qz.printers.find();
      })
      .then((printers) => {
        if (!printers || !Array.isArray(printers)) {
          console.error("Printer list is invalid or undefined:", printers);
          toast.error("Failed to fetch printer list.");
          return;
        }

        console.log("Printers found:", printers);
        setPrinterList(printers);

        const storedPrinter = localStorage.getItem("selectedPrinter");

        if (storedPrinter && printers.includes(storedPrinter)) {
          setSelectedPrinter(storedPrinter);
        } else {
          setSelectedPrinter(printers[0]);
        }
      })
      .catch((err) => {
        console.error("QZ Tray Error:", err);
        toast.error("QZ Tray Error: " + err.message);
      });
  }, []);

  // signed certificate for qz tray

  useEffect(() => {
    // Check if a connection is already open
    if (!qz.websocket.isActive()) {
      qz.websocket.connect().then(() => {
        return qz.printers.find().catch((err) => toast.error(err.message));
      });
    } else {
    }
  }, []);
  return (
    <>
      <PageLoader />
      <Suspense
        fallback={
          <Loading
            color={"rgba(1, 18, 34, 1)"}
            size={42}
            height={true}
            flex={true}
          />
        }
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<ProtectedRoutes />}>
            <Route
              path="/"
              element={
                <Home
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/sorter-session"
              element={
                <SorterSession
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/add-session/:id?"
              element={
                <AddSession
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/dock-summary"
              element={
                <DockSummary
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/add-dock"
              element={
                <AddDock
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/setting"
              element={
                <Settings
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/sortation-plan/:id?"
              element={
                <SortationPlan
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/active-session/:id?"
              element={
                <ActiveSession
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/creation"
              element={
                <Creation
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
            <Route
              path="/summary"
              element={
                <Summary
                  printerList={printerList}
                  selectedPrinter={selectedPrinter}
                  setSelectedPrinter={setSelectedPrinter}
                />
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => {
  return (
    <Router>
      <Layout />
    </Router>
  );
};

export default App;
